/* eslint-disable no-nested-ternary */
import { useScreenSize } from '@hooks';
import { Button, Flex, theme, Typography } from '@styles';
import { ReactComponent as Arrow } from '@svgs/pixel_arrow.svg';
import { arrayOf, func, object } from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';

import { FrameContainer, StyledIcon } from './AnimatedCardsFrame.layoutProps';
import CardDefinitionWithAnimation from './CardDefinitionWithAnimation';

// Example of the object from data array
//   {
//     definition:
//       'The rules of war, also known as international humanitarian law (IHL) or the law of armed conflict (LOAC), are a set of international guidelines that dictate permissible and prohibited actions during an armed conflict. These rules are essential for maintaining order and a certain level of humanity even in times of war.',
//     statement: '1Explaining the Rules of War',
//     topText: 'Lesson 1. Are there any rules in war?',
//     yellowWordIndexes: [2, 4],
//   },

const AnimatedCardsFrame = ({ data, getActiveCard, onFinish }) => {
  const { dynamicWidth } = useScreenSize();
  const [activeIndex, setActiveIndex] = useState(0);
  const [skippedCards, setSkippedCards] = useState([]);
  const [buttonsBlocked, setButtonsBlocked] = useState(false);
  const [isBackDirection, setIsBackDirection] = useState(false);

  const length = data?.length || 0;

  const memoCards = useMemo(
    () =>
      data?.map((item, index) => (
        <CardDefinitionWithAnimation
          key={index}
          data={item}
          direction={index % 2 === 0 ? 1 : -1}
          isActive={activeIndex === index}
          isBackDirection={isBackDirection}
          isLastSlide={index === length - 1}
          isSkipped={skippedCards.includes(index)}
          onFinish={onFinish}
          setButtonsBlocked={setButtonsBlocked}
          zIndex={-(index + 1)}
        />
      )),
    [data, skippedCards, activeIndex, isBackDirection, onFinish]
  );

  useEffect(() => {
    getActiveCard?.(data?.[activeIndex]);
  }, [activeIndex, data]);

  const onSkip = (back) => () => {
    setIsBackDirection(!!back);
    setActiveIndex((prevIndex) => {
      const newIndex = (prevIndex + (back ? -1 : 1)) % length;
      if (newIndex === 0) {
        setSkippedCards([0]);
      }

      if (!back) {
        setSkippedCards((prev) =>
          prev.includes(prevIndex) ? prev : [...prev, prevIndex]
        );
      }
      return newIndex;
    });
  };

  const handleLeftButton = () => {
    setButtonsBlocked(true);
    onSkip(true)();
  };

  const handleRightButton = () => {
    setButtonsBlocked(true);
    onSkip()();
  };

  return (
    <Flex flexDirection="column" gap={24}>
      <Flex alignItems="center" gap={32} justifyContent="center" width="100%">
        <Button
          disabled={buttonsBlocked || activeIndex === 0}
          gap={24}
          onClick={handleLeftButton}
          variant="textButton"
        >
          <StyledIcon
            disabled={buttonsBlocked || activeIndex === 0}
            height={40}
            right
            SVG={Arrow}
            width={40}
          />
        </Button>
        <Typography
          color={theme.colors.white[100]}
          opacity="0.6"
          variant="MS-regular-s32-lh50"
        >
          {activeIndex + 1}/{length}
        </Typography>
        <Button
          disabled={buttonsBlocked || length - 1 === activeIndex}
          onClick={handleRightButton}
          variant="textButton"
        >
          <StyledIcon
            disabled={buttonsBlocked || length - 1 === activeIndex}
            height={40}
            SVG={Arrow}
            width={40}
          />
        </Button>
      </Flex>
      <FrameContainer dynamicWidth={dynamicWidth}>
        {memoCards?.map((el) => el)}
      </FrameContainer>
    </Flex>
  );
};

AnimatedCardsFrame.propTypes = {
  data: arrayOf(object).isRequired,
  getActiveCard: func,
  onFinish: func,
};

AnimatedCardsFrame.defaultProps = {
  getActiveCard: null,
  onFinish: null,
};

export default AnimatedCardsFrame;
